import {
  type SupplierServiceApi,
  Configuration,
  SupplierServiceApiFactory
} from '@amici/myamici-integrations-client'
import { type AxiosPromise } from 'axios'
import { useAuth } from 'react-oidc-context'
import { useEnv } from './useEnv'
import {
  type AccountsApi,
  AccountsApiFactory,
  type MetadataApi,
  MetadataApiFactory
} from '@amici/myamici-api-client'

export interface UseApiHook {
  accountsApi: AccountsApi
  suppliersApi: SupplierServiceApi
  metadataApi: MetadataApi

  fetcher: <P = any, R = any>(
    method: (params: P) => AxiosPromise<R>,
    params: P
  ) => Promise<R>

  fetcherWithoutParams: <R = any>(method: AxiosPromise<R>) => Promise<R>
}

function useApi (): UseApiHook {
  const { user } = useAuth()
  const env = useEnv()

  const apiConfig = new Configuration({
    accessToken: user?.access_token,
    ...(!!env.REACT_APP_API_HOST && { basePath: env.REACT_APP_API_HOST })
  })

  const integrationsApiConfig = new Configuration({
    accessToken: user?.access_token,
    ...(!!env.REACT_APP_INTEGRATIONS_API_HOST && {
      basePath: env.REACT_APP_INTEGRATIONS_API_HOST
    })
  })

  async function fetcher<P = any, R = any> (
    method: (params: P) => AxiosPromise<R>,
    params: P
  ): Promise<R> {
    const { data } = await method(params)
    return data
  }

  async function fetcherWithoutParams<R = any> (
    method: AxiosPromise<R>
  ): Promise<R> {
    const { data } = await method
    return data
  }

  return {
    accountsApi: AccountsApiFactory(apiConfig) as AccountsApi,
    suppliersApi: SupplierServiceApiFactory(
      integrationsApiConfig
    ) as SupplierServiceApi,
    metadataApi: MetadataApiFactory(apiConfig) as MetadataApi,
    fetcher,
    fetcherWithoutParams
  }
}
export default useApi
